import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { Login } from './components/Login';
import { Register } from './components/Register';
import { Dashboard } from './components/Dashboard';
import { LoadingScreen } from './components/LoadingScreen';
import { AcceptInvitation } from './components/AcceptInvitation';
import { AITrackingWidgetPage } from './components/AITrackingWidgetPage';
import { aiTrackingService } from './services/aiTrackingService';
import { personalizationService, PersonalizationSettings } from './services/personalizationService';

// Force rebuild to apply vercel.json routing changes

// Helper to load cached personalization from window.__BOOT_THEME__ or localStorage
const loadCachedPersonalization = (): PersonalizationSettings | null => {
  // Priority 1: window.__BOOT_THEME__ (set by index.html boot script)
  const bootTheme = (window as any).__BOOT_THEME__;
  if (bootTheme) {
    console.log('🎨 App: Using window.__BOOT_THEME__');
    return {
      theme: bootTheme,
      branding: {}, // Will be loaded from backend
    } as PersonalizationSettings;
  }

  // Priority 2: localStorage fallback
  try {
    const cached = localStorage.getItem('personalization-settings');
    if (cached) {
      console.log('🎨 App: Using localStorage cache');
      return JSON.parse(cached);
    }
  } catch (error) {
    console.warn('Failed to load cached personalization:', error);
  }
  
  console.log('🎨 App: No cached personalization found');
  return null;
};

const AppContent: React.FC = () => {
  const { state, logout } = useAuth();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [showInvitationPrompt, setShowInvitationPrompt] = useState(false);
  const [invitationToken, setInvitationToken] = useState<string | null>(null);

  // Check for team invitation parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const invitation = urlParams.get('invitation');
    const token = urlParams.get('token');
    const invitationToken = urlParams.get('invitationToken');
    
    console.log('🔍 App: Checking URL params:', { 
      url: window.location.href, 
      search: window.location.search,
      invitation, 
      token,
      invitationToken,
      isAuthenticated: state.isAuthenticated 
    });
    
    // Direct register flow with invitation token
    if (invitationToken && !state.isAuthenticated) {
      console.log('✅ App: Found invitationToken in URL, showing Register');
      setIsLoginMode(false);
      return;
    }
    
    // New token-based invitation flow
    if (token) {
      console.log('✅ App: Found invitation token, mounting AcceptInvitation');
      setInvitationToken(token);
      return;
    }
    
    // Legacy invitation flow
    if (invitation === 'true' && state.isAuthenticated) {
      setShowInvitationPrompt(true);
    } else if (invitation === 'true' && !state.isAuthenticated) {
      setIsLoginMode(false); // Show register form for invitations
    }
  }, [state.isAuthenticated]);

  // Auto-restart AI tracking if it was running before
  useEffect(() => {
    if (state.isAuthenticated) {
      const status = aiTrackingService.getStatus();
      if (status.isRunning && status.config) {
        // Restart AI tracking with saved config
        aiTrackingService.start(status.config);
      }
    }
  }, [state.isAuthenticated]);

  // Auto-detect and save timezone on login
  useEffect(() => {
    if (state.isAuthenticated) {
      const detectAndSaveTimezone = async () => {
        try {
          const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          console.log('🕐 App: Detected browser timezone:', browserTimezone);
          
          // Save to backend
          await fetch('/api/account/settings', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ timezone: browserTimezone })
          });
          
          console.log('✅ App: Timezone saved to backend');
        } catch (error) {
          console.error('❌ App: Failed to save timezone:', error);
        }
      };
      
      detectAndSaveTimezone();
    }
  }, [state.isAuthenticated]);

  // Handle invitation token flow
  if (invitationToken) {
    return (
      <AcceptInvitation 
        token={invitationToken} 
        onComplete={() => {
          // Clear token from URL and redirect to dashboard
          window.history.replaceState({}, '', window.location.pathname);
          setInvitationToken(null);
        }} 
      />
    );
  }

  // 🔒 CRITICAL: Show loading screen while auth is verifying token
  // This prevents WorkspaceProvider from mounting before auth is ready
  // MUST be after all hooks to follow Rules of Hooks
  if (state.loading) {
    console.log('🔒 App: Auth loading, showing LoadingScreen');
    return <LoadingScreen />;
  }

  // Handle invitation prompt for already logged in users
  if (showInvitationPrompt) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <h2 style={{ marginBottom: '20px', color: '#e67e22' }}>🎉 Team Invitation</h2>
          <p style={{ marginBottom: '30px', lineHeight: '1.6' }}>
            You've been invited to join a team! You're currently logged in as <strong>{state.user?.name}</strong>.
          </p>
          <p style={{ marginBottom: '30px', lineHeight: '1.6' }}>
            Would you like to:
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                setShowInvitationPrompt(false);
                // Clear invitation parameter and go to dashboard
                window.history.replaceState({}, '', window.location.pathname);
              }}
              style={{
                background: '#e67e22',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Accept with current account
            </button>
            <button
              onClick={() => {
                logout();
                setShowInvitationPrompt(false);
                setIsLoginMode(false);
              }}
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Log out & create new account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 🔒 CRITICAL: Wrap authenticated content with WorkspaceProvider
  // This ensures WorkspaceProvider only mounts AFTER auth is verified
  if (state.isAuthenticated) {
    // 🪟 Widget Mode: If path is /widget, show standalone widget
    if (window.location.pathname === '/widget') {
      return (
        <WorkspaceProvider>
          <AITrackingWidgetPage />
        </WorkspaceProvider>
      );
    }

    return (
      <WorkspaceProvider>
        <Dashboard />
      </WorkspaceProvider>
    );
  }

  return isLoginMode ? (
    <Login onToggleMode={() => setIsLoginMode(false)} />
  ) : (
    <Register onToggleMode={() => setIsLoginMode(true)} />
  );
};

function App() {
  // Theme is now managed by index.tsx
  // If we have cached settings, we're ready immediately (no initialization delay)
  const cachedSettings = loadCachedPersonalization();
  const [isInitializing, setIsInitializing] = useState(!cachedSettings);

  // Initialize personalization - optimized for zero flash
  useEffect(() => {
    const cachedSettingsForEffect = cachedSettings;
    const initializePersonalization = async () => {
      try {
        // If we have cache, just verify and update from backend silently
        if (cachedSettingsForEffect) {
          console.log('🎨 App: Cache loaded, skipping re-fetch (theme already applied by boot script)');
          // Cache exists and was already applied by boot script in index.html
          // No need to fetch again - this prevents double loading
          setIsInitializing(false);
        } else {
          // No cache - fetch from backend (only on first visit or after cache clear)
          console.log('🎨 App: No cache, fetching from backend...');
          
          const token = localStorage.getItem('token');
          if (token) {
            await personalizationService.initializePersonalization();
            const settings = await personalizationService.getPersonalizationSettings();
            console.log('🎨 App: Settings loaded:', settings);
            
            // Cache for next time and reload to apply boot script theme
            localStorage.setItem('personalization-settings', JSON.stringify(settings));
            window.location.reload();
          }
          
          setIsInitializing(false);
        }
      } catch (error) {
        console.error('🎨 App: Failed to initialize personalization:', error);
        setIsInitializing(false);
      }
    };

    initializePersonalization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Listen for theme updates from authenticated user
  useEffect(() => {
    const handleThemeUpdate = (event: CustomEvent<PersonalizationSettings>) => {
      const newSettings = event.detail;
      console.log('🎨 App: Received personalization update:', newSettings);
      
      // Update cache (theme is already applied via personalizationService.applyThemeToDOM)
      // No reload needed - MUI theme updates dynamically
      localStorage.setItem('personalization-settings', JSON.stringify(newSettings));
      console.log('🎨 App: Theme cached (no reload - applied dynamically)');
    };

    window.addEventListener('personalization-updated' as any, handleThemeUpdate);
    
    return () => {
      window.removeEventListener('personalization-updated' as any, handleThemeUpdate);
    };
  }, []);

  // Show loading screen during initialization
  if (isInitializing) {
    return <LoadingScreen />;
  }

  // ThemeProvider is now in index.tsx - we just return the content
  // WorkspaceProvider moved inside AppContent to prevent premature mounting
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
